/**
 * #670 — attempt lifecycle for the #300 org automation wind-down.
 *
 * Module-level (Zustand) on purpose: the POST can run for minutes, and the
 * verdict — residue order IDs + the durable audit operationId — must survive
 * the operator navigating tabs or an admin switching orgs mid-run. There is
 * no relay GET for past wind-downs, so a lost response is unrecoverable
 * client-side. The in-flight guard also lives here so a remount can never
 * fire a duplicate POST. Nothing is persisted: a full page refresh clears
 * this store and repeats nothing.
 */
import { create } from 'zustand';
import { apiFetch } from './authStore';
import type { OrgOption } from './orgScope';
import {
  parseAutomationWindDownResult,
  type AutomationWindDownResult,
} from '../types/automationWindDown';

export type WindDownFailureClass =
  | 'session-expired'
  | 'not-authorized'
  | 'already-running'
  | 'org-not-found'
  | 'endpoint-missing'
  | 'gateway-unavailable'
  | 'request-rejected'
  | 'server-error'
  | 'network'
  | 'unknown-response';

export type WindDownAttempt =
  | { kind: 'running'; target: OrgOption; startedAt: number }
  | { kind: 'result'; target: OrgOption; result: AutomationWindDownResult }
  | { kind: 'failure'; target: OrgOption; failure: WindDownFailureClass; serverMessage?: string };

/**
 * 409's HTTP surface code is AUTOMATION_WIND_DOWN_IN_PROGRESS (manager.ts:3119);
 * the fence error's own code is AUTOMATION_WIND_DOWN_ACTIVE — key off HTTP status.
 * serverMessage only ever carries a relay `error` string (human-authored copy),
 * never an exception message.
 */
export function classifyWindDownFailure(
  status: number,
  body: { error?: string; code?: string },
): { failure: WindDownFailureClass; serverMessage?: string } {
  if (status === 401) return { failure: 'session-expired' };
  if (status === 403) return { failure: 'not-authorized' };
  if (status === 409) return { failure: 'already-running' };
  if (status === 404) {
    return body.code === 'AUTOMATION_WIND_DOWN_ORG_NOT_FOUND'
      ? { failure: 'org-not-found' }
      : { failure: 'endpoint-missing' };
  }
  if (status === 502 || status === 503 || status === 504) return { failure: 'gateway-unavailable' };
  if (status === 400) return { failure: 'request-rejected', serverMessage: body.error };
  return { failure: 'server-error' };
}

interface WindDownState {
  attemptsByOrg: Record<string, WindDownAttempt>;
  lastResultByOrg: Record<string, { target: OrgOption; result: AutomationWindDownResult }>;
  run: (target: OrgOption, opts: { admin: boolean }) => Promise<void>;
}

export const useWindDownStore = create<WindDownState>((set, get) => ({
  attemptsByOrg: {},
  lastResultByOrg: {},
  run: async (target, { admin }) => {
    if (get().attemptsByOrg[target.id]?.kind === 'running') return; // exactly-once per org, remount-proof
    const setAttempt = (attempt: WindDownAttempt) =>
      set((s) => ({ attemptsByOrg: { ...s.attemptsByOrg, [target.id]: attempt } }));
    setAttempt({ kind: 'running', target, startedAt: Date.now() });
    try {
      const query = admin ? `?orgId=${target.id}` : '';
      const res = await apiFetch(`/api/manager/org/automation-wind-down${query}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: true }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
        setAttempt({ kind: 'failure', target, ...classifyWindDownFailure(res.status, data) });
        return;
      }
      const parsed = parseAutomationWindDownResult(await res.json().catch(() => null));
      if (!parsed) {
        setAttempt({ kind: 'failure', target, failure: 'unknown-response' });
        return;
      }
      set((s) => ({
        attemptsByOrg: { ...s.attemptsByOrg, [target.id]: { kind: 'result', target, result: parsed } },
        lastResultByOrg: { ...s.lastResultByOrg, [target.id]: { target, result: parsed } },
      }));
    } catch {
      setAttempt({ kind: 'failure', target, failure: 'network' });
    }
  },
}));
